/**
 * The color of the answer avatars, as plain data. No React, no user.
 *
 * Every avatar is the app's doctor placeholder redrawn in brand tokens, see `SpecialistFigure`: a
 * circle, a figure on it, and a ring of the surface's own gap around it. The palette is six named
 * variants, defined once here: three bases, a deep circle with a pale figure and a ring in the
 * circle color, and their three inverses, a pale circle with a deep figure and a ring in a mid step
 * of the group. Each part is one token, which the theme resolves per mode; the ramps invert between
 * light and dark, so a base is deep in light and pale in dark, and an inverse the reverse.
 *
 * In use today, Primum Specialist 1 is `green`, 2 `blue` and 3 `orange`, then round again, and
 * from 24px up a specialist carries its number in a badge filled in its circle color. The
 * submitter is `paleGreen`, drawn with no ring and no badge, so a ring always means a specialist:
 * ringed, a pale green asker read as the same green as a green specialist in a reply pair. `paleBlue`
 * and `paleOrange` are defined and tested but drawn nowhere yet. The role is always said by the
 * label beside the avatar, so who is speaking never rests on color alone.
 *
 * A specialist's variant is a function of the number a reader sees, never of an account, a row id
 * or a place in a list that can shift: Primum Specialist 2 is blue in its card
 * header and on every reply it makes, and is blue on every post.
 *
 * **Why these steps.** The brand blue and green are both muted, so the bases cannot sit at the
 * steps a designer would reach for first. Navy `secondary-800` and the placeholder's own green
 * `primary-600` are only CIEDE2000 16.8 apart in light, under the 20 the bases must keep, and
 * `primary-600` as a ring reads 4.25:1 on the header strip in dark, under the 4.5:1 ring bar. The
 * only pair that clears both bars in both themes is a mid slate `secondary-600` and a near-black
 * green `primary-900`, 20.7 apart at the closest. Orange is `tertiary-700`, not the brand
 * `tertiary-500`, which is within CIEDE2000 4 of `warning-500` and takes white text at only 3.3:1.
 * For the same ring bar the inverse rings are `secondary-600` and `primary-700`, since 400 and 500
 * read only 2.2 to 4.3:1 on the card.
 *
 * **The inverses are not told apart by color.** Their circles sit close together: `paleBlue` and
 * `paleGreen` are CIEDE2000 9.1 apart in light and 11.3 in dark, `paleBlue` and `paleOrange` 25.6
 * and 41.8, `paleGreen` and `paleOrange` 22.2 and 42.2. No spread is required among them, so
 * anything that ever shows two inverses side by side must tell them apart by label, as the answers
 * do. The submitter's `paleGreen` stays 36.6 or more from every base in both themes.
 *
 * `answersContrast.test.ts` holds every figure to 3:1 on its circle, every ring to 4.5:1 on the
 * card and the header strip, every base badge numeral to 4.5:1 and the bases 20 apart, in both
 * themes.
 */

export type AvatarRole = 'specialist' | 'submitter';

/** The six avatar variants: circle, figure and ring, each one brand token. */
export const AVATAR_VARIANTS = {
  blue: { circle: 'secondary-600', figure: 'secondary-100', ring: 'secondary-600' },
  green: { circle: 'primary-900', figure: 'primary-50', ring: 'primary-900' },
  orange: { circle: 'tertiary-700', figure: 'tertiary-100', ring: 'tertiary-700' },
  paleBlue: { circle: 'secondary-100', figure: 'secondary-700', ring: 'secondary-600' },
  paleGreen: { circle: 'primary-100', figure: 'primary-700', ring: 'primary-700' },
  paleOrange: { circle: 'tertiary-200', figure: 'tertiary-800', ring: 'tertiary-700' },
} as const;

export type AvatarVariantName = keyof typeof AVATAR_VARIANTS;

export type AvatarVariant = (typeof AVATAR_VARIANTS)[AvatarVariantName];

/** Any token an avatar part is drawn in. */
export type AvatarToken = AvatarVariant[keyof AvatarVariant];

/** The bases, for Primum Specialists 1, 2 and 3 in number order. */
export const BASE_VARIANTS = ['blue', 'green', 'orange'] as const satisfies AvatarVariantName[];

/**
 * The bases a Primum Specialist takes, in turn by number. A green specialist and the pale green
 * submitter stay apart because only a specialist is ringed.
 */
export const SPECIALIST_VARIANTS = [
  'green',
  'blue',
  'orange',
] as const satisfies AvatarVariantName[];

/** The inverses, in the same group order as the bases. */
export const INVERSE_VARIANTS = [
  'paleBlue',
  'paleGreen',
  'paleOrange',
] as const satisfies AvatarVariantName[];

/** The submitter's avatar, in the question card and on every submitter reply. */
export const SUBMITTER_VARIANT = 'paleGreen' satisfies AvatarVariantName;

/** The circle tokens of the bases, which are also their ring and badge fill, in number order. */
export const SPECIALIST_TONES = ['secondary-600', 'primary-900', 'tertiary-700'] as const;

export type SpecialistTone = (typeof SPECIALIST_TONES)[number];

/**
 * The token of the number drawn in each badge. `background-0` is white in light and near black in
 * dark, which suits a fill that is deep in light and pale in dark, as all three are.
 */
export const BADGE_GLYPH_TONES: Record<SpecialistTone, string> = {
  'secondary-600': 'background-0',
  'primary-900': 'background-0',
  'tertiary-700': 'background-0',
};

/** The badge's thin border, which parts it from the figure underneath. */
export const BADGE_BORDER_TONE = 'background-0';

/**
 * The variant for a Primum Specialist number, 1-based: 1 green, 2 blue, 3 orange, 4 green again. A
 * fourth specialist shares the first one's color and is told apart by its number and its name.
 */
export function specialistVariant(position: number): (typeof SPECIALIST_VARIANTS)[number] {
  const count = SPECIALIST_VARIANTS.length;

  const place = (((Math.trunc(position) - 1) % count) + count) % count;

  return SPECIALIST_VARIANTS[Number.isFinite(place) ? place : 0];
}

/** The tone, the circle, ring and badge token, for a Primum Specialist number. */
export function specialistTone(position: number): SpecialistTone {
  return AVATAR_VARIANTS[specialistVariant(position)].circle;
}

/*
 * Classes written out whole so Tailwind finds every one in the source and the colors come from
 * the theme's CSS variables, which follow light and dark.
 */

export const RING_CLASS: Record<AvatarVariantName, string> = {
  blue: 'border-secondary-600',
  green: 'border-primary-900',
  orange: 'border-tertiary-700',
  paleBlue: 'border-secondary-600',
  paleGreen: 'border-primary-700',
  paleOrange: 'border-tertiary-700',
};

export const BADGE_FILL_CLASS: Record<SpecialistTone, string> = {
  'secondary-600': 'bg-secondary-600',
  'primary-900': 'bg-primary-900',
  'tertiary-700': 'bg-tertiary-700',
};

export const BADGE_GLYPH_CLASS: Record<SpecialistTone, string> = {
  'secondary-600': 'text-background-0',
  'primary-900': 'text-background-0',
  'tertiary-700': 'text-background-0',
};

export const BADGE_BORDER_CLASS = 'border-background-0';
